import type { NodeViewProps } from '@tiptap/react';

import { mergeAttributes, Node } from '@tiptap/core';
import '@tiptap/markdown';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';

export const NP_AT_HANDLE_TAG = 'np-at-handle';

export interface MessageMarkdownMentionAttrs {
  userId: string;
  userName: string;
}

export interface MessageMentionSuggestionItem {
  id: string;
  userId: string;
  userName: string;
  displayName: string;
  label: string;
}

function escapeHtmlAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function normalizeHandle(value: unknown): string {
  if (typeof value !== 'string') {
    return '';
  }

  return value
    .trim()
    .replace(/^@+/, '')
    .replace(/\s+/g, '')
    .replace(/[^a-zA-Z0-9._-]/g, '')
    .slice(0, 64);
}

function normalizeMentionUserId(value: unknown): string {
  if (typeof value !== 'string') {
    return '';
  }

  return value.trim().slice(0, 128);
}

function parseMentionTagAttributes(
  rawAttrs: string,
): MessageMarkdownMentionAttrs {
  return normalizeMessageMarkdownMentionAttrs({
    userId:
      /\buser-id="([^"]*)"/.exec(rawAttrs)?.[1] ??
      /\buserid="([^"]*)"/.exec(rawAttrs)?.[1],
    userName:
      /\buser-name="([^"]*)"/.exec(rawAttrs)?.[1] ??
      /\busername="([^"]*)"/.exec(rawAttrs)?.[1] ??
      /\bhandle="([^"]*)"/.exec(rawAttrs)?.[1],
  });
}

export function normalizeMessageMarkdownMentionAttrs(
  attrs: unknown,
): MessageMarkdownMentionAttrs {
  const rec =
    attrs && typeof attrs === 'object'
      ? (attrs as Record<string, unknown>)
      : {};

  return {
    userId: normalizeMentionUserId(rec.userId ?? rec['user-id']),
    userName: normalizeHandle(rec.userName ?? rec['user-name'] ?? rec.handle),
  };
}

export function renderMessageMarkdownMentionTag(
  userId: string,
  userName: string,
): string {
  const normalizedUserId = normalizeMentionUserId(userId);
  const normalizedUserName = normalizeHandle(userName);
  if (!normalizedUserId || !normalizedUserName) {
    return '';
  }

  return `<${NP_AT_HANDLE_TAG} user-id="${escapeHtmlAttr(normalizedUserId)}" user-name="${escapeHtmlAttr(normalizedUserName)}"></${NP_AT_HANDLE_TAG}>`;
}

export function buildMessageMarkdownMentionPreview(markdown: string): string {
  return markdown.replace(
    /<np-at-handle\s+([^>]*)><\/np-at-handle>/g,
    (_match: string, rawAttrs: string) => {
      const attrs = parseMentionTagAttributes(rawAttrs);
      if (!attrs.userName) {
        return '';
      }

      return `<span class="np-inline-handle" data-handle="${escapeHtmlAttr(attrs.userName)}"></span>`;
    },
  );
}

function MessageMarkdownMentionNodeView({ node }: NodeViewProps) {
  const attrs = normalizeMessageMarkdownMentionAttrs(node.attrs);

  return (
    <NodeViewWrapper
      as="span"
      className="message-markdown-mention"
      contentEditable={false}
      data-drag-handle={false}
      data-handle={attrs.userName}
      data-user-id={attrs.userId}
    >
      <span className="message-markdown-mention__chip">{`@${attrs.userName}`}</span>
    </NodeViewWrapper>
  );
}

export const MessageMarkdownMentionNode = Node.create({
  name: 'npAtHandle',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: false,
  isolating: true,
  defining: true,

  addAttributes() {
    return {
      userId: {
        default: '',
      },
      userName: {
        default: '',
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: NP_AT_HANDLE_TAG,
        getAttrs: (element) => {
          if (!(element instanceof HTMLElement)) {
            return false;
          }

          return normalizeMessageMarkdownMentionAttrs({
            userId:
              element.getAttribute('user-id') ?? element.getAttribute('userid'),
            userName:
              element.getAttribute('user-name') ??
              element.getAttribute('username') ??
              element.getAttribute('handle'),
          });
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const attrs = normalizeMessageMarkdownMentionAttrs(HTMLAttributes);
    return [
      NP_AT_HANDLE_TAG,
      mergeAttributes({
        'user-id': attrs.userId,
        'user-name': attrs.userName,
      }),
    ];
  },

  renderMarkdown(node) {
    const attrs = normalizeMessageMarkdownMentionAttrs(node?.attrs);
    return renderMessageMarkdownMentionTag(attrs.userId, attrs.userName);
  },

  addNodeView() {
    return ReactNodeViewRenderer(MessageMarkdownMentionNodeView);
  },
});
